import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  Music,
  Calendar,
  ShoppingBag,
  MessageSquare,
  CalendarCheck,
  Users,
  Settings,
  LogOut,
  X,
  Mic2,
  FileCheck,
} from 'lucide-react';

const navItems = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/music', label: 'Music', icon: Music },
  { path: '/admin/shows', label: 'Shows', icon: Calendar },
  { path: '/admin/merch', label: 'Merchandise', icon: ShoppingBag },
  { path: '/admin/bookings', label: 'Bookings', icon: Mic2 },
  { path: '/admin/contracts', label: 'Contracts', icon: FileCheck },
  { path: '/admin/contacts', label: 'Contacts', icon: MessageSquare },
  { path: '/admin/calendar', label: 'Calendar', icon: CalendarCheck },
];

const adminOnlyItems = [
  { path: '/admin/users', label: 'Team Members', icon: Users },
  { path: '/admin/settings', label: 'Settings', icon: Settings },
];

export default function AdminSidebar({ isOpen, onClose }) {
  const { user, userRole, logout, isAdmin } = useAuth();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
  };

  const NavItem = ({ item }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;

    return (
      <NavLink
        to={item.path}
        onClick={() => onClose?.()}
        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
          isActive
            ? 'bg-gradient-to-r from-orange-500/20 to-red-600/20 text-orange-500 border border-orange-500/30'
            : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
        }`}
      >
        <Icon className="w-5 h-5" />
        <span className="font-medium">{item.label}</span>
      </NavLink>
    );
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center">
              <Music className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-white">Admin</h1>
              <p className="text-xs text-gray-500">Scooby DaGodSon</p>
            </div>
          </a>
          {/* Mobile close button */}
          {onClose && (
            <button
              onClick={onClose}
              className="md:hidden p-2 text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Main Menu
        </p>
        {navItems.map((item) => (
          <NavItem key={item.path} item={item} />
        ))}

        {/* Admin Only Section */}
        {isAdmin && (
          <>
            <p className="px-4 py-2 mt-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Administration
            </p>
            {adminOnlyItems.map((item) => (
              <NavItem key={item.path} item={item} />
            ))}
          </>
        )}
      </nav>

      {/* User Info & Logout */}
      <div className="p-4 border-t border-gray-800">
        <div className="flex items-center gap-3 px-4 py-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center">
            <span className="text-white font-semibold">
              {user?.displayName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">
              {user?.displayName || user?.email}
            </p>
            <p className="text-xs text-gray-500 capitalize">{userRole}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 w-full px-4 py-3 text-gray-400 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-all"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">Sign Out</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-[#0a0a0a] border-r border-gray-800 h-screen fixed left-0 top-0 z-40">
        {sidebarContent}
      </aside>

      {/* Mobile Sidebar Overlay */}
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden"
          />
          <motion.aside
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed left-0 top-0 w-64 h-screen bg-[#0a0a0a] border-r border-gray-800 z-50 md:hidden"
          >
            {sidebarContent}
          </motion.aside>
        </>
      )}
    </>
  );
}
